import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * "🔖 Save this search" button for the search/browse results header.
 * Spec: WEB_MOBILE_SPEC_SAVED_SEARCHES.
 *
 * Props:
 *   criteria: { q?, category_slug?, region?, city?, min_price?, max_price? }
 * Backend requires at least ONE of q / category_slug to be set — the button
 * hides itself otherwise.
 */
export default function SaveSearchButton({ criteria }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  // Only meaningful when at least q or category_slug is present.
  // Empty strings must be normalised to `undefined` — SearchPage forwards
  // `?min_price=&max_price=` verbatim, and the backend's numeric schema
  // rejects empty strings with a 422.
  const blank = (v) => (v === undefined || v === null || v === "" ? undefined : v);
  const trimmed = useMemo(() => ({
    q: (criteria?.q || "").trim() || undefined,
    category_slug: blank(criteria?.category_slug),
    region: blank(criteria?.region),
    city: blank(criteria?.city),
    min_price: blank(criteria?.min_price),
    max_price: blank(criteria?.max_price),
  }), [criteria]);

  const savedQ = useQuery({
    queryKey: ["saved-searches", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/saved-searches");
      return data?.saved_searches || [];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });

  const existing = useMemo(() => {
    if (!savedQ.data) return null;
    return savedQ.data.find((s) =>
      (s.q || "") === (trimmed.q || "") &&
      (s.category_slug || null) === (trimmed.category_slug || null) &&
      (s.region || null) === (trimmed.region || null) &&
      (s.city || null) === (trimmed.city || null) &&
      (s.min_price ?? null) === (trimmed.min_price ?? null) &&
      (s.max_price ?? null) === (trimmed.max_price ?? null)
    ) || null;
  }, [savedQ.data, trimmed]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/saved-searches", trimmed);
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["saved-searches", user?.id] });
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      if (data?.already_saved) {
        toast.info("You've already saved this search.");
      } else {
        toast.success("Saved — we'll alert you when new matches go live.");
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't save this search"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => sharedApi.delete(`/classifieds/saved-searches/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-searches", user?.id] });
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      toast.success("Saved search removed.");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't remove"),
  });

  // Hide the button when no meaningful criteria to save
  const hasCriteria = !!(trimmed.q || trimmed.category_slug);
  if (!hasCriteria) return null;

  const onClick = () => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (existing) deleteMut.mutate(existing.id);
    else saveMut.mutate();
  };

  const busy = saveMut.isPending || deleteMut.isPending || (user && savedQ.isLoading);
  const isSaved = !!existing;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="save-search-btn"
      aria-pressed={isSaved}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-70 ${
        isSaved
          ? "bg-[color:var(--brand-blue)] text-white hover:bg-[color:var(--brand-blue-hover)]"
          : "bg-white text-slate-700 border border-soft hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)]"
      }`}
      title={isSaved ? "We're alerting you about new matches — click to remove" : "Get emailed when new matches go live"}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isSaved ? (
        <BookmarkCheck className="w-4 h-4" />
      ) : (
        <Bookmark className="w-4 h-4" />
      )}
      {isSaved ? "Saved — we'll alert you" : "Save this search"}
    </button>
  );
}
